import { useCallback, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import { ProductVideoCard } from "~/components/common/ProductVideoCard";
import { EntertainmentVideoCard } from "~/components/common/EntertainmentVideoCard";
import { FullscreenVideoModal } from "~/components/common/FullscreenVideoModal";
import { fetchWatchFeed, type WatchFeedItem } from "~/api/watch";
import { reportUnexpectedError } from "~/api/client";
import { useUserStore } from "~/lib/stores/user";
import { VideoCardSkeleton } from "~/components/skeleton/VideoCardSkeleton";
import { isEntertainment } from "~/lib/utils";

export function VideoDealsSection() {
  // A getter, not a subscription: the token is only needed at request time, and having
  // it as a dependency made the effect fire once before the session settled and again
  // after, discarding the first result.
  const readToken = useCallback(() => useUserStore.getState().token, []);
  const [items, setItems] = useState<WatchFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<WatchFeedItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    fetchWatchFeed(readToken)
      .then(data => {
        if (!cancelled) setItems(data.feedItems ?? []);
      })
      .catch(error => {
        reportUnexpectedError(error);
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [readToken]);

  if (!isLoading && items.length === 0) return null;

  const header = (
    <h2 className="mb-4 flex items-center justify-between text-3xl font-bold tracking-tight">
      <span>Video deals</span>
      <Link to="/watch" className="flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-neutral-700">
        See all
        <ChevronRight className="size-3.5" />
      </Link>
    </h2>
  );

  if (isLoading) {
    return (
      <section>
        {header}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <VideoCardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      {header}
      <div className="group">
        <Carousel
          opts={{
            align: "start",
            loop: false,
            dragFree: true,
            containScroll: "trimSnaps",
            duration: 20,
            watchResize: false,
            watchSlides: false,
          }}
          className="relative w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {items.map(item => (
              <CarouselItem
                key={item.feedItemId}
                className="basis-1/2 pl-2 md:basis-1/3 md:pl-4 lg:basis-1/4 xl:basis-1/5"
              >
                {isEntertainment(item) ? (
                  <EntertainmentVideoCard
                    data={{
                      feedItemId: item.feedItemId,
                      videoUrl: item.videoUrl ?? "",
                      thumbnail: item.thumbnail,
                      viewCount: item.viewCount,
                      likeCount: item.likeCount,
                    }}
                    onClick={() => setSelectedItem(item)}
                  />
                ) : (
                  <ProductVideoCard
                    data={{
                      feedItemId: item.feedItemId,
                      videoUrl: item.videoUrl ?? "",
                      title: item.title ?? "",
                      viewCount: item.viewCount,
                      likeCount: item.likeCount,
                    }}
                    onClick={() => setSelectedItem(item)}
                  />
                )}
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious
            variant="carouselNav"
            size="icon"
            customPosition={true}
            className="absolute top-[40%] -left-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
          />
          <CarouselNext
            variant="carouselNav"
            size="icon"
            customPosition={true}
            className="absolute top-[40%] -right-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
          />
        </Carousel>
      </div>

      {selectedItem && (
        <FullscreenVideoModal
          open={!!selectedItem}
          onOpenChange={open => {
            if (!open) setSelectedItem(null);
          }}
          surface="deal_section_modal"
          data={{
            feedItemId: selectedItem.feedItemId,
            videoUrl: selectedItem.videoUrl ?? "",
            thumbnail: selectedItem.thumbnail,
            title: !isEntertainment(selectedItem) ? selectedItem.title : undefined,
            dealId: selectedItem.dealId,
            viewCount: selectedItem.viewCount,
            likeCount: selectedItem.likeCount,
            contentType: isEntertainment(selectedItem) ? "entertainment" : "product",
          }}
        />
      )}
    </section>
  );
}
